/*
 * Converts between the stored dynamic-form text format (see formText.js) and the HTML the
 * rich text editor edits. The stored format is unchanged so existing forms render as before:
 *   **bold**  *italic*  __underline__  ##large##  [text](url)  "- " bullets, one paragraph per line.
 *
 * Where two markers would touch and confuse the page's parser (bold next to italic, say) a
 * zero-width space is placed between them. It is invisible on the page and removed on load.
 */
import { splitParagraphs, isBulletLine, isNumberedLine, numberOf, stripNumber } from './formText';

const ZWSP = '​';

const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const plain = (s) => esc(s.split(ZWSP).join(''));

// Same pattern as parseInlineFormatting, so the editor reads text exactly as the page does.
// eslint-disable-next-line no-useless-escape
const FORMAT_RE = /(\[([^\]]+)\]\(([^)]+)\)|\*\*.*?\*\*|\##.*?\##|__.*?__|(?<!\*)\*(?!\*).*?(?<!\*)\*(?!\*))/g;

const inlineToHtml = (text) => {
  let html = '';
  let last = 0;
  for (const match of text.matchAll(FORMAT_RE)) {
    const full = match[0];
    if (match.index > last) html += plain(text.substring(last, match.index));
    if (full.startsWith('[') && match[2] && match[3]) {
      html += `<a href="${esc(match[3])}">${plain(match[2])}</a>`;
    } else if (full.startsWith('**') && full.endsWith('**')) {
      const inner = full.slice(2, -2);
      html += inner.startsWith('##') && inner.endsWith('##')
        ? `<b><font size="5">${plain(inner.slice(2, -2))}</font></b>`
        : `<b>${inlineToHtml(inner)}</b>`;
    } else if (full.startsWith('##') && full.endsWith('##')) {
      const inner = full.slice(2, -2);
      html += inner.startsWith('**') && inner.endsWith('**')
        ? `<font size="5"><b>${plain(inner.slice(2, -2))}</b></font>`
        : `<font size="5">${inlineToHtml(inner)}</font>`;
    } else if (full.startsWith('__') && full.endsWith('__')) {
      html += `<u>${inlineToHtml(full.slice(2, -2))}</u>`;
    } else if (full.startsWith('*') && full.endsWith('*')) {
      html += `<i>${inlineToHtml(full.slice(1, -1))}</i>`;
    } else {
      html += plain(full);
    }
    last = match.index + full.length;
  }
  if (last < text.length) html += plain(text.substring(last));
  return html;
};

/** Stored text to editor HTML: one <div> per paragraph, bullets in a <ul>, numbered lines in an <ol>. */
export const storedToHtml = (value) => {
  const lines = splitParagraphs(value);
  if (lines.length === 0) return '<div><br></div>';
  let html = '';
  let list = null; // { tag, start, items }
  const flush = () => {
    if (list) {
      const start = list.tag === 'ol' && list.start !== 1 ? ` start="${list.start}"` : '';
      html += `<${list.tag}${start}>${list.items.map((li) => `<li>${li}</li>`).join('')}</${list.tag}>`;
    }
    list = null;
  };
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (isBulletLine(trimmed)) {
      if (list && list.tag !== 'ul') flush();
      if (!list) list = { tag: 'ul', items: [] };
      list.items.push(inlineToHtml(trimmed.replace(/^[-•]\s+/, '')) || '<br>');
    } else if (isNumberedLine(trimmed)) {
      if (list && list.tag !== 'ol') flush();
      if (!list) list = { tag: 'ol', start: numberOf(trimmed), items: [] };
      list.items.push(inlineToHtml(stripNumber(trimmed)) || '<br>');
    } else {
      flush();
      html += `<div>${inlineToHtml(trimmed)}</div>`;
    }
  });
  flush();
  return html;
};

const BLOCK_TAGS = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE']);

const isLargeSize = (fontSize) => {
  if (!fontSize) return null;
  if (/large|larger/.test(fontSize) && !/x-small|small/.test(fontSize)) return true;
  const n = parseFloat(fontSize);
  if (Number.isNaN(n)) return null;
  if (/em|rem/.test(fontSize)) return n > 1.1;
  if (/px/.test(fontSize)) return n >= 18;
  if (/%/.test(fontSize)) return n > 110;
  return null;
};

const marksOf = (el, marks) => {
  const next = { ...marks };
  const tag = el.tagName;
  if (tag === 'B' || tag === 'STRONG') next.b = true;
  if (tag === 'I' || tag === 'EM') next.i = true;
  if (tag === 'U' || tag === 'INS') next.u = true;
  if (tag === 'FONT' && el.getAttribute('size')) next.l = Number(el.getAttribute('size')) >= 4;
  const style = el.style;
  if (style) {
    const weight = style.fontWeight;
    if (weight) next.b = weight === 'bold' || weight === 'bolder' || Number(weight) >= 600;
    if (style.fontStyle) next.i = style.fontStyle === 'italic' || style.fontStyle === 'oblique';
    const deco = style.textDecorationLine || style.textDecoration;
    if (deco && deco.includes('underline')) next.u = true;
    const large = isLargeSize(style.fontSize);
    if (large !== null) next.l = large;
  }
  return next;
};

const sameMarks = (a, b) => !!a.b === !!b.b && !!a.i === !!b.i && !!a.u === !!b.u && !!a.l === !!b.l;

/**
 * Collects the editor DOM into blocks of runs:
 * [{ type: 'p' | 'li' | 'ol', n?, runs: [{ text, marks, href? }] }]. Numbered items carry their number.
 */
const collectBlocks = (root) => {
  const blocks = [];
  let cur = null;
  let olNext = null; // the next number in the numbered list being read
  const start = (type) => {
    cur = { type: type === 'li' && olNext !== null ? 'ol' : type, runs: [] };
    if (cur.type === 'ol') cur.n = olNext++;
    blocks.push(cur);
  };

  const walk = (node, marks, inList) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        if (!cur) start(inList ? 'li' : 'p');
        cur.runs.push({ text: child.nodeValue, marks });
        return;
      }
      if (child.nodeType !== 1) return;
      const tag = child.tagName;
      if (tag === 'BR') {
        cur = null;
      } else if (tag === 'UL' || tag === 'OL') {
        cur = null;
        const outer = olNext;
        olNext = tag === 'OL' ? Number(child.getAttribute('start')) || 1 : null;
        walk(child, marks, true);
        olNext = outer;
        cur = null;
      } else if (tag === 'LI') {
        start('li');
        walk(child, marks, true);
        cur = null;
      } else if (BLOCK_TAGS.has(tag)) {
        // A block inside a list item continues that item; otherwise it is a new paragraph.
        if (!(inList && cur && cur.runs.length === 0)) start(inList ? 'li' : 'p');
        walk(child, marks, inList);
        cur = null;
      } else if (tag === 'A') {
        if (!cur) start(inList ? 'li' : 'p');
        cur.runs.push({ text: child.textContent, marks: marksOf(child, marks), href: child.getAttribute('href') || '' });
      } else {
        walk(child, marksOf(child, marks), inList);
      }
    });
  };

  walk(root, {}, false);
  return blocks;
};

const MARKER_END = /[*_#]$/;
const MARKER_START = /^[*_#]/;

const wrap = (core, m) => {
  let s = core;
  if (m.i) s = `*${s}*`;
  if (m.u) s = `__${s}__`;
  if (m.b) s = m.i && !m.u ? `**${ZWSP}${s}${ZWSP}**` : `**${s}**`;
  // "##**x**##" is read as plain large bold text, so keep a separator when bold holds more formatting
  if (m.l) s = m.b && (m.i || m.u) ? `##${ZWSP}${s}${ZWSP}##` : `##${s}##`;
  return s;
};

const serializeRuns = (runs) => {
  // merge neighbouring plain runs that share formatting
  const merged = [];
  runs.forEach((run) => {
    const text = run.text.split(ZWSP).join('').replace(/ /g, ' ').replace(/\s+/g, ' ');
    const prev = merged[merged.length - 1];
    if (prev && !prev.href && !run.href && sameMarks(prev.marks, run.marks)) prev.text += text;
    else merged.push({ ...run, text });
  });
  // the stored format has no way to keep two spaces in a row (they start a new paragraph)
  for (let k = 1; k < merged.length; k += 1) {
    if (merged[k - 1].text.endsWith(' ') && merged[k].text.startsWith(' ')) merged[k].text = merged[k].text.slice(1);
  }

  let out = '';
  merged.forEach((run) => {
    let piece;
    if (run.href) {
      const label = run.text.replace(/[[\]]/g, '').trim();
      const href = run.href.trim().replace(/\)/g, '%29').replace(/\s/g, '%20');
      piece = label && href ? wrap(`[${label}](${href})`, run.marks) : label;
    } else {
      const lead = run.text.match(/^\s*/)[0];
      const trail = run.text.slice(lead.length).match(/\s*$/)[0];
      const core = run.text.slice(lead.length, run.text.length - trail.length);
      const marked = run.marks.b || run.marks.i || run.marks.u || run.marks.l;
      piece = core && marked ? `${lead}${wrap(core, run.marks)}${trail}` : run.text;
    }
    if (out && MARKER_END.test(out) && MARKER_START.test(piece)) out += ZWSP;
    out += piece;
  });
  return out.trim();
};

/** Editor DOM to stored text. */
export const domToStored = (root) =>
  collectBlocks(root)
    .map((block) => {
      const text = serializeRuns(block.runs);
      if (!text) return null;
      if (block.type === 'li') return `- ${text}`;
      if (block.type === 'ol') return `${block.n}. ${text}`;
      return text;
    })
    .filter(Boolean)
    .join('\n');
